import { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '../ui/Button';

interface StripePaymentFormProps {
  amount: number;
  currency: string;
  invoiceId: number;
  onSuccess: () => void;
  onError: (error: string) => void;
  onCancel: () => void;
}

export function StripePaymentForm({
  amount,
  currency,
  invoiceId,
  onSuccess,
  onError,
  onCancel,
}: StripePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      onError('Stripe is not loaded yet. Please try again.');
      return;
    }

    setProcessing(true);

    try {
      // Confirm the payment
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/client/invoices/${invoiceId}`,
        },
      });

      if (error) {
        onError(error.message || 'Payment failed');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess();
      } else if (paymentIntent && paymentIntent.status === 'requires_action') {
        // 3D Secure authentication in progress
        onError('Payment requires additional authentication. Please complete the authentication and try again.');
      } else {
        onError('Payment processing failed. Please try again.');
      }
    } catch (error: any) {
      onError(error.message || 'An unexpected error occurred');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-slate-800/80 border border-slate-700/50 rounded-lg p-6 space-y-4">
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card'],
          }}
        />
        <div className="bg-blue-900/20 border border-blue-700 rounded p-3 text-sm text-blue-300">
          🔒 Your payment information is encrypted and secure
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={processing}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="success"
          disabled={!stripe || processing}
          className="flex-1"
        >
          {processing ? 'Processing...' : `Pay ${new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)}`}
        </Button>
      </div>
    </form>
  );
}
